import {
  Money,
  PaymentOption,
  PaymentPlan,
  PaymentStatus,
  PaymentTypeEnum,
  Quote,
  StringIndexableDictionary,
} from "../Types";
import { useDatasources } from "../../contexts/StoryblokContext/StoryblokContext";
import { LabelKey } from "../StoryblokTypes";
import { ColorVariant } from "../services/ColorService";
import { FormattedMoney } from "./MoneyHelper";
import { ReactNode } from "react";
import { Text } from "@ilc-technology/luik";

export type PaymentDetails = {
  status?: PaymentStatus;
  icon: string;
  color: ColorVariant;
  headerMessage: string;
  contentMessage?: string;
  statusText?: string;
};

export const constructPaymentDetails = (status: PaymentStatus | null): PaymentDetails => {
  const { labels } = useDatasources();

  switch (status) {
    case PaymentStatus.PaymentSuccess:
      return {
        status: PaymentStatus.PaymentSuccess,
        headerMessage: labels[LabelKey.paymentStatus_PaymentSuccessHeader],
        icon: "check",
        color: ColorVariant.GreenDark,
        statusText: labels[LabelKey.paid],
      };
    case PaymentStatus.PaymentProcessed:
      return {
        status: PaymentStatus.PaymentProcessed,
        headerMessage: labels[LabelKey.paymentStatus_PaymentProcessedHeader],
        icon: "hourglass",
        color: ColorVariant.YellowDark,
        statusText: labels[LabelKey.pending],
      };
    case PaymentStatus.ReservationSuccess:
      return {
        status: PaymentStatus.ReservationSuccess,
        headerMessage: labels[LabelKey.paymentStatus_ReservationSuccessHeader],
        icon: "locked",
        color: ColorVariant.DefaultDark,
        statusText: labels[LabelKey.toBePaid],
      };
    // Should never reach this case,
    // unless the payment summary page is accessed directly, without the query params
    default:
      return {
        icon: "support",
        color: ColorVariant.RedDark,
        headerMessage:
          "There was an issue displaying the payment status at the moment. Please contact support if you have any questions.",
        statusText: "Unknown",
      };
  }
};

type PaymentOptionRadio = {
  title: string;
  price: Money;
  subtitle: string;
  key: PaymentOption;
  testId: string;
};
export const createPaymentOptions = ({
  labels,
  reservationPrice,
  totalPrice,
  isRecurringPaymentEnabled,
  isCustomPaymentEnabled,
  isWithinRecurringPaymentWindow,
  isWithinSplitPaymentWindow,
  isSponsoredOpportunity,
  isPaidInFullHidden = false,
  isLegacyPaymentPlanHidden = false,
}: {
  labels: StringIndexableDictionary<string>;
  reservationPrice: Money;
  totalPrice: Money;
  isCustomPaymentEnabled: boolean;
  isRecurringPaymentEnabled: boolean;
  isWithinRecurringPaymentWindow: boolean;
  isWithinSplitPaymentWindow: boolean;
  isSponsoredOpportunity: boolean;
  isPaidInFullHidden?: boolean;
  isLegacyPaymentPlanHidden?: boolean;
}) => {
  // Legacy payment plan = anything sent to the backend as "Payment plan payments"
  const isHiddenLegacyPaymentPlan = (option: PaymentOption, plan: PaymentPlan = PaymentPlan.Monthly) =>
    isLegacyPaymentPlanHidden && resolvePaymentType(option, plan, false) === PaymentTypeEnum.PAYMENT_PLAN;

  const paymentConfig: Record<PaymentOption, StringIndexableDictionary<string>> = {
    [PaymentOption.FullPayment]: {
      title: labels[LabelKey.fullPayment],
      subtitle: labels[LabelKey.fullPaymentDetails],
    },
    [PaymentOption.FullPaymentWithDiscount]: {
      title: labels[LabelKey.fullPayment],
      subtitle: labels[LabelKey.fullPaymentDetails],
    },
    [PaymentOption.SplitPayment]: {
      title: labels[LabelKey.splitYourPayment],
      subtitle: labels[LabelKey.splitYourPaymentDetails],
    },
    [PaymentOption.ScholarshipFunding]: {
      title: labels[LabelKey.scholarshipFunding],
      subtitle: labels[LabelKey.scholarshipDetails],
    },
    [PaymentOption.DownPayment]: {
      title: labels[LabelKey.downPayment],
      subtitle: labels[LabelKey.downPaymentDetails],
    },
    [PaymentOption.CustomAmountPayment]: {
      title: labels[LabelKey.payCustomAmountTitle],
      subtitle: labels[LabelKey.payCustomAmountSubtitle],
    },
  };

  const notEligibleForRecurringPaymentOptions = [
    PaymentOption.FullPayment,
    PaymentOption.DownPayment,
    isCustomPaymentEnabled && PaymentOption.CustomAmountPayment,
  ];

  const eligibleForRecurringPaymentOptions = [
    PaymentOption.FullPayment,
    isWithinSplitPaymentWindow && PaymentOption.SplitPayment,
    isSponsoredOpportunity && PaymentOption.ScholarshipFunding,
  ];

  const computedOptions = isRecurringPaymentEnabled
    ? eligibleForRecurringPaymentOptions
    : notEligibleForRecurringPaymentOptions;

  const availablePaymentTypes = computedOptions.filter((option) => Boolean(option)) as PaymentOption[];
  const visiblePaymentTypes = availablePaymentTypes.filter(
    (option) => !(isPaidInFullHidden && option === PaymentOption.FullPayment) && !isHiddenLegacyPaymentPlan(option)
  );
  // Never leave the student without any way to pay: fall back to full payment if every option is hidden
  const conditionalPaymentTypes = visiblePaymentTypes.length > 0 ? visiblePaymentTypes : [PaymentOption.FullPayment];

  const paymentOptions: PaymentOptionRadio[] = conditionalPaymentTypes.map((key) => ({
    key,
    title: paymentConfig[key].title,
    subtitle: paymentConfig[key].subtitle,
    price: key === PaymentOption.FullPayment ? totalPrice : reservationPrice,
    testId: `${key}-payment-option`,
  }));

  const paymentPlans = [
    {
      key: PaymentPlan.Monthly,
      title: labels[isWithinRecurringPaymentWindow ? LabelKey.monthlyPayments : LabelKey.deferredPayment],
      subtitle:
        labels[isWithinRecurringPaymentWindow ? LabelKey.monthlyPaymentsDetails : LabelKey.deferredPaymentDetails],
      price: reservationPrice,
      testId: "monthly-payment-value",
    },
  ];

  if (isWithinRecurringPaymentWindow) {
    paymentPlans.push({
      key: PaymentPlan.Installments,
      title: labels[LabelKey.installmentPayments],
      subtitle: labels[LabelKey.installmentPaymentsDetails],
      price: reservationPrice,
      testId: "installment-payment-value",
    });
  }

  return {
    paymentOptions,
    paymentPlans: paymentPlans.filter((plan) => !isHiddenLegacyPaymentPlan(PaymentOption.SplitPayment, plan.key)),
  };
};

export const getPaymentOptionDescription = ({
  option,
  quote,
  reservationPrice,
  paymentValue,
  amountConfirmed,
  chosenPaymentOption,
  payInFullDiscountAmount,
  localAmounts,
}: {
  option: PaymentOption;
  quote: Quote;
  reservationPrice: Money;
  paymentValue: Money;
  amountConfirmed: boolean;
  chosenPaymentOption: PaymentOption;
  payInFullDiscountAmount: number;
  localAmounts?: { totalPrice: Money; discountedPrice: Money; reservationPrice: Money };
}): ReactNode | null => {
  const hasDiscount = !!payInFullDiscountAmount;
  const discountedPrice = localAmounts?.discountedPrice ?? { ...quote.prices!.totalPrice! };
  if (!localAmounts && hasDiscount) {
    discountedPrice.amount -= Math.abs(payInFullDiscountAmount);
  }
  const formattedTotalPrice = FormattedMoney(localAmounts?.totalPrice ?? quote.prices?.totalPrice);
  const formattedDiscountedPrice = FormattedMoney(discountedPrice);
  switch (option) {
    case PaymentOption.FullPayment:
      return !hasDiscount ? (
        formattedTotalPrice
      ) : (
        <div className="flex flex-row gap-2">
          <Text variant="label-lg" className="whitespace-nowrap line-through">
            {formattedTotalPrice}
          </Text>
          <Text variant="label-lg-bold" className="whitespace-nowrap text-success">
            {formattedDiscountedPrice}
          </Text>
        </div>
      );

    case PaymentOption.CustomAmountPayment:
      return amountConfirmed && chosenPaymentOption === PaymentOption.CustomAmountPayment
        ? FormattedMoney(paymentValue)
        : null;

    default:
      return FormattedMoney(localAmounts?.reservationPrice ?? reservationPrice);
  }
};

export const paymentTypeMap: Record<PaymentOption, PaymentTypeEnum> = {
  [PaymentOption.CustomAmountPayment]: PaymentTypeEnum.PAYMENT_PLAN,
  [PaymentOption.DownPayment]: PaymentTypeEnum.PAYMENT_PLAN,
  [PaymentOption.FullPayment]: PaymentTypeEnum.PAID_IN_FULL,
  [PaymentOption.SplitPayment]: PaymentTypeEnum.RECURRING_MONTHLY,
  [PaymentOption.ScholarshipFunding]: PaymentTypeEnum.SPONSORED_PAYMENT_PLAN,
  [PaymentOption.FullPaymentWithDiscount]: PaymentTypeEnum.PAID_IN_FULL_WITH_DISCOUNT,
};

export const resolvePaymentType = (
  paymentOption: PaymentOption,
  paymentPlan: PaymentPlan,
  hasDiscount: boolean
): PaymentTypeEnum => {
  if (paymentOption === PaymentOption.FullPayment && hasDiscount) {
    return PaymentTypeEnum.PAID_IN_FULL_WITH_DISCOUNT;
  }

  if (paymentOption === PaymentOption.SplitPayment && paymentPlan === PaymentPlan.Installments) {
    return PaymentTypeEnum.PAYMENT_PLAN;
  }

  return paymentTypeMap[paymentOption];
};
